import { useCallback } from "react";
import {
  FormattedMessage,
  FormattedNumber,
  MessageDescriptor,
  defineMessage,
  useIntl,
} from "react-intl";
import { cn } from "../utils/tailwind";
import { graphql, useFragment, useMutation } from "react-relay";
import { RecordSourceSelectorProxy } from "relay-runtime";
import { toast } from "sonner";
import {
  VoteDisplayFragment$data,
  VoteDisplayFragment$key,
} from "./__generated__/VoteDisplayFragment.graphql";
import { useAuth, useLoginRedirect } from "../utils/auth";
import {
  VoteDisplayPublishMutation as VoteDisplayPublishMutationType,
  VoteKind,
} from "./__generated__/VoteDisplayPublishMutation.graphql";
import { VoteDisplayResetMutation as VoteDisplayResetMutationType } from "./__generated__/VoteDisplayResetMutation.graphql";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownDoubleIcon,
  ChevronUpDoubleIcon,
} from "@/icons";
import { Link } from "react-router";

const Fragment = graphql`
  fragment VoteDisplayFragment on Votable {
    id
    votes
    voterCount
    voters(first: 10) {
      ...EntitiesAvatarFragment
    }
    voted @ifAllowed {
      score
    }
    viewerCanVote: viewerCan(action: PUBLISH_VOTE)
  }
`;

const PublishMutation = graphql`
  mutation VoteDisplayPublishMutation($id: ID!, $kind: VoteKind!) {
    publishVote(id: $id, kind: $kind) {
      node {
        id
        ...VoteDisplayFragment
      }
    }
  }
`;

const ResetMutation = graphql`
  mutation VoteDisplayResetMutation($id: ID!) {
    resetVote(id: $id) {
      node {
        id
        ...VoteDisplayFragment
      }
    }
  }
`;

type VoteDisplayKind =
  | "readOnly"
  | "compact"
  | "vertical"
  | "horizontal"
  | "horizontal-upvote";

/**
 * How the viewer can interact with the vote controls:
 * - `vote`: signed in and allowed to vote, buttons publish/reset the vote.
 * - `login`: signed out, buttons send to the login page and back here.
 * - `readOnly`: signed in but not allowed to vote, only the score is shown.
 */
type VoteMode = "vote" | "login" | "readOnly";

export interface VoteDisplayProps {
  subject: VoteDisplayFragment$key;
  kind?: VoteDisplayKind;
  to?: string;
}

export default function VoteDisplay({
  subject: subjectFragment,
  kind,
  to,
}: VoteDisplayProps) {
  const { isAuthenticated } = useAuth();
  const subject = useFragment(Fragment, subjectFragment);
  const mode: VoteMode = !isAuthenticated
    ? "login"
    : subject.viewerCanVote
      ? "vote"
      : "readOnly";

  if (!kind || kind === "readOnly" || mode === "readOnly") {
    if (!subject.votes) {
      return null;
    }
    return <ReadonlyVoteDisplay votes={subject.votes} to={to} />;
  }

  switch (kind) {
    case "compact":
      return <CompactVoteDisplay subject={subject} mode={mode} />;
    case "vertical":
      return <VerticalVoteDisplay subject={subject} mode={mode} />;
    case "horizontal":
      return <HorizontalVoteDisplay subject={subject} mode={mode} />;
    case "horizontal-upvote":
      return <HorizontalUpVoteOnlyDisplay subject={subject} mode={mode} />;
  }
}

interface InteractiveVoteDisplayProps {
  subject: VoteDisplayFragment$data;
  mode: Exclude<VoteMode, "readOnly">;
}

const ReadonlyVoteDisplay = ({ votes, to }: { votes: number; to?: string }) => {
  const intl = useIntl();
  const className = "flex items-center border rounded-lg overflow-hidden";
  const content = (
    <>
      <span
        className={cn(
          "flex items-center",
          votes === 0 ? "border-0" : "border-r",
        )}
        title={intl.formatMessage({ defaultMessage: "votes" })}
      >
        <VoteAmount votes={votes} className="w-[5ch]" />
      </span>
      {votes > 0 ? (
        <ArrowUpIcon className="px-2 w-8" />
      ) : votes < 0 ? (
        <ArrowDownIcon className="px-2 w-8" />
      ) : null}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cn(className, "hover:cursor-pointer")}>
        {content}
      </Link>
    );
  }
  return <div className={className}>{content}</div>;
};

/**
 * Upvote toggle sized for cards in listings: a single button holding both
 * the icon and the score, so the whole control is one click target.
 */
const CompactVoteDisplay = ({ subject, mode }: InteractiveVoteDisplayProps) => {
  const { votes, id } = subject;
  const score = subject.voted?.score ?? 0;
  const [vote, isVoting] = useVote(id, score);

  return (
    <VoteButton
      kind="UPVOTE"
      score={score}
      mode={mode}
      isVoting={isVoting}
      onVote={vote}
      className="w-auto gap-1 px-2 py-1 border rounded-lg focus:rounded-lg"
    >
      <span className="font-mono text-grey-700">
        <FormattedNumber value={votes} notation="compact" />
      </span>
    </VoteButton>
  );
};

const HorizontalUpVoteOnlyDisplay = ({
  subject,
  mode,
}: InteractiveVoteDisplayProps) => {
  const intl = useIntl();
  const { votes, id } = subject;
  const score = subject.voted?.score ?? 0;
  const [vote, isVoting] = useVote(id, score);

  return (
    <div className="flex flex-row items-center border rounded-lg overflow-hidden">
      <VoteButton
        kind="UPVOTE"
        score={score}
        mode={mode}
        isVoting={isVoting}
        onVote={vote}
        className="focus:rounded-l-lg"
      />

      <span
        className="px-5 border-l flex flex-row items-center justify-center text-base leading-normal font-semibold text-grey-700"
        title={intl.formatMessage({ defaultMessage: "votes" })}
      >
        <FormattedMessage defaultMessage="Upvoted" />
        <VoteAmount votes={votes} className="w-[2ch] text-brand-500" />
      </span>
    </div>
  );
};

const HorizontalVoteDisplay = ({
  subject,
  mode,
}: InteractiveVoteDisplayProps) => {
  const intl = useIntl();
  const { votes, id } = subject;
  const score = subject.voted?.score ?? 0;
  const [vote, isVoting] = useVote(id, score);

  return (
    <div className="flex flex-row items-center border rounded-lg overflow-hidden">
      <VoteButton
        kind="UPVOTE"
        score={score}
        mode={mode}
        isVoting={isVoting}
        onVote={vote}
        className="focus:rounded-l-lg"
      />
      <span
        className="border-l border-r border-grey-300"
        title={intl.formatMessage({ defaultMessage: "votes" })}
      >
        <VoteAmount votes={votes} className="w-[5ch]" />
      </span>
      <VoteButton
        kind="DOWNVOTE"
        score={score}
        mode={mode}
        isVoting={isVoting}
        onVote={vote}
        className="focus:rounded-r-lg"
      />
    </div>
  );
};

const VerticalVoteDisplay = ({
  subject,
  mode,
}: InteractiveVoteDisplayProps) => {
  const intl = useIntl();
  const { votes, id } = subject;
  const score = subject.voted?.score ?? 0;
  const [vote, isVoting] = useVote(id, score);

  return (
    <div className="flex flex-col items-center border rounded-lg overflow-hidden">
      <VoteButton
        kind="UPVOTE"
        score={score}
        mode={mode}
        isVoting={isVoting}
        onVote={vote}
        className="focus:rounded-t-lg"
      />
      <span
        className="border-t border-b border-grey-300"
        title={intl.formatMessage({ defaultMessage: "votes" })}
      >
        <VoteAmount votes={votes} className="w-[5ch]" />
      </span>
      <VoteButton
        kind="DOWNVOTE"
        score={score}
        mode={mode}
        isVoting={isVoting}
        onVote={vote}
        className="focus:rounded-b-lg"
      />
    </div>
  );
};

const VoteAmount = ({
  votes,
  className,
}: {
  votes: number;
  className?: string;
}) => (
  <p
    className={cn("font-mono py-2 text-center cursor-default", className)}
    onClick={(event) => event.stopPropagation()}
  >
    <FormattedNumber value={votes} notation="compact" />
  </p>
);

const UpvoteIcon = ChevronUpDoubleIcon;
const DownvoteIcon = ChevronDownDoubleIcon;

function voteKindScore(kind: VoteKind) {
  switch (kind) {
    case "UPVOTE":
      return 1;
    case "DOWNVOTE":
      return -1;
  }
}

/**
 * Applies the vote locally before the server answers so the control reacts
 * instantly. Relay rolls it back on its own if the mutation fails.
 */
function applyOptimisticVote(
  store: RecordSourceSelectorProxy,
  id: string,
  previousScore: number,
  nextScore: number,
) {
  const record = store.get(id);
  if (!record) {
    return;
  }
  const votes = record.getValue("votes");
  if (typeof votes === "number") {
    record.setValue(votes - previousScore + nextScore, "votes");
  }
  const voterCount = record.getValue("voterCount");
  if (typeof voterCount === "number") {
    record.setValue(
      voterCount - Math.abs(previousScore) + Math.abs(nextScore),
      "voterCount",
    );
  }
  if (nextScore === 0) {
    record.setValue(null, "voted");
    return;
  }
  const votedId = `client:optimisticVote:${id}`;
  const voted =
    record.getLinkedRecord("voted") ??
    store.get(votedId) ??
    store.create(votedId, "EntityVote");
  voted.setValue(nextScore, "score");
  record.setLinkedRecord(voted, "voted");
}

function useVote(id: string, score: number) {
  const intl = useIntl();
  const [publishMutation, isPublishing] =
    useMutation<VoteDisplayPublishMutationType>(PublishMutation);
  const [resetMutation, isResetting] =
    useMutation<VoteDisplayResetMutationType>(ResetMutation);

  const vote = useCallback(
    (kind: VoteKind) => {
      const isReset = score === voteKindScore(kind);
      const nextScore = isReset ? 0 : voteKindScore(kind);
      const config = {
        optimisticUpdater: (store: RecordSourceSelectorProxy) =>
          applyOptimisticVote(store, id, score, nextScore),
        onError: () => {
          toast.error(
            intl.formatMessage({
              defaultMessage: "Your vote could not be saved. Please try again.",
            }),
          );
        },
      };
      if (isReset) {
        resetMutation({ variables: { id }, ...config });
      } else {
        publishMutation({ variables: { id, kind }, ...config });
      }
    },
    [id, score, intl, publishMutation, resetMutation],
  );

  return [vote, isPublishing || isResetting] as const;
}

interface VoteButtonProps {
  kind: VoteKind;
  score: number;
  mode: Exclude<VoteMode, "readOnly">;
  isVoting: boolean;
  onVote: (kind: VoteKind) => void;
  className?: string;
  children?: React.ReactNode;
}

const VoteButton = ({
  kind,
  score,
  mode,
  isVoting,
  onVote,
  className,
  children,
}: VoteButtonProps) => {
  const intl = useIntl();
  const loginPath = useLoginRedirect();
  const isActive = score === voteKindScore(kind);
  const classNames = cn(
    "flex justify-center items-center w-full focus:outline-hidden focus:-outline-offset-1 h-full px-3 py-2",
    voteButtonClassNames(isActive),
    className,
  );
  const content = (
    <>
      <VoteIcon kind={kind} isActive={isActive} />
      {children}
    </>
  );

  if (mode === "login") {
    const label = intl.formatMessage(voteButtonLoginTitle(kind));
    return (
      <Link
        to={loginPath}
        rel="nofollow"
        className={classNames}
        title={label}
        aria-label={label}
      >
        {content}
      </Link>
    );
  }

  const label = intl.formatMessage(voteButtonTitle(kind));
  return (
    <button
      type="button"
      className={classNames}
      title={label}
      aria-label={label}
      aria-pressed={isActive}
      disabled={isVoting}
      onClick={(event) => {
        // Vote controls sit inside clickable cards and list items.
        event.preventDefault();
        event.stopPropagation();
        onVote(kind);
      }}
    >
      {content}
    </button>
  );
};

function voteButtonClassNames(isActive: boolean) {
  return isActive
    ? "text-brand-500 bg-brand-25 hover:bg-brand-50 focus:outline-brand-100 hover:border-brand-100"
    : "text-white bg-white hover:bg-grey-50 focus:outline-grey-300 hover:border-grey-300 ";
}

function voteButtonTitle(kind: VoteKind): MessageDescriptor {
  switch (kind) {
    case "UPVOTE":
      return defineMessage({ defaultMessage: "upvote" });
    case "DOWNVOTE":
      return defineMessage({ defaultMessage: "downvote" });
  }
}

function voteButtonLoginTitle(kind: VoteKind): MessageDescriptor {
  switch (kind) {
    case "UPVOTE":
      return defineMessage({ defaultMessage: "Log in to upvote" });
    case "DOWNVOTE":
      return defineMessage({ defaultMessage: "Log in to downvote" });
  }
}

const VoteIcon = ({
  kind,
  isActive,
}: {
  kind: VoteKind;
  isActive: boolean;
}) => {
  const className = cn(isActive ? "text-brand-500" : "text-grey-700");
  switch (kind) {
    case "UPVOTE":
      return <UpvoteIcon className={className} />;
    case "DOWNVOTE":
      return <DownvoteIcon className={className} />;
  }
};
